import { useEffect, useState } from 'react';
import { FiAlertTriangle, FiMapPin, FiServer, FiSettings, FiUsers } from 'react-icons/fi';
import type { IconType } from 'react-icons';
import type { AuthFetch, NamedItem, Pop, StockMovement, User, UserStock } from '../../types';
import { Button, ModalBackdrop, Select, TextInput } from '../ui';
import { cn } from '../../lib/cn';

interface SettingsPageProps {
  authFetch: AuthFetch;
  users: User[];
  pops: Pop[];
  cities: NamedItem[];
  occurrenceTypes: NamedItem[];
  reloadUsers: () => Promise<void>;
  reloadPops: () => Promise<void>;
  reloadCities: () => Promise<void>;
  reloadOccurrenceTypes: () => Promise<void>;
}

type Section = 'users' | 'pops' | 'cities' | 'types';

const SECTIONS: { id: Section; label: string; desc: string; Icon: IconType }[] = [
  { id: 'users', label: 'Usuários', desc: 'Contas e permissões', Icon: FiUsers },
  { id: 'pops', label: 'POPs', desc: 'Pontos de presença', Icon: FiServer },
  { id: 'cities', label: 'Cidades', desc: 'Cidades atendidas', Icon: FiMapPin },
  { id: 'types', label: 'Tipos de Ocorrência', desc: 'Catálogo de ocorrências', Icon: FiAlertTriangle }
];

const thClass = 'px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500';
const tdClass = 'px-4 py-2.5 text-sm text-slate-700';
const REASON_LABEL: Record<string, string> = { entrada: 'Entrada', montagem: 'Montagem', ajuste: 'Ajuste' };

export default function SettingsPage({
  authFetch,
  users,
  pops,
  cities,
  occurrenceTypes,
  reloadUsers,
  reloadPops,
  reloadCities,
  reloadOccurrenceTypes
}: SettingsPageProps) {
  const [section, setSection] = useState<Section>('users');

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden md:flex-row">
      <aside className="flex flex-shrink-0 flex-col border-b border-slate-200 bg-white md:w-64 md:border-b-0 md:border-r">
        <div className="hidden items-center gap-2 px-5 py-4 text-base font-bold text-slate-800 md:flex">
          <FiSettings size={18} /> Configurações
        </div>
        <nav className="flex flex-wrap gap-1 p-2 md:flex-col md:flex-nowrap md:gap-0.5 md:p-3">
          {SECTIONS.map(({ id, label, desc, Icon }) => {
            const active = section === id;
            return (
              <button
                key={id}
                onClick={() => setSection(id)}
                className={cn(
                  'flex flex-1 items-center gap-3 rounded-lg px-3 py-2.5 text-left transition md:flex-none',
                  active ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-100'
                )}
              >
                <span
                  className={cn(
                    'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg',
                    active ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'
                  )}
                >
                  <Icon size={18} />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold">{label}</span>
                  <span className="hidden text-xs text-slate-400 md:block">{desc}</span>
                </span>
              </button>
            );
          })}
        </nav>
      </aside>

      <div className="min-h-0 flex-1 overflow-auto p-3 sm:p-5">
        {section === 'users' && <UsersSection authFetch={authFetch} users={users} reloadUsers={reloadUsers} />}
        {section === 'pops' && (
          <NamedCrudSection
            authFetch={authFetch}
            items={pops}
            reload={reloadPops}
            endpoint="/pops"
            title="POPs"
            description="Gerencie os pontos de presença usados nas caixas e relatórios."
            columnLabel="POP"
            placeholder="nome do POP"
          />
        )}
        {section === 'cities' && (
          <NamedCrudSection
            authFetch={authFetch}
            items={cities}
            reload={reloadCities}
            endpoint="/cities"
            title="Cidades"
            description="Cidades atendidas, usadas no cadastro de ocorrências."
            columnLabel="Cidade"
            placeholder="nome da cidade"
          />
        )}
        {section === 'types' && (
          <NamedCrudSection
            authFetch={authFetch}
            items={occurrenceTypes}
            reload={reloadOccurrenceTypes}
            endpoint="/occurrence-types"
            title="Tipos de Ocorrência"
            description="Catálogo de tipos de ocorrência usados nas OS de rede."
            columnLabel="Tipo"
            placeholder="ex: MONTAR CTO"
          />
        )}
      </div>
    </div>
  );
}

/* ─── Usuários ───────────────────────────────────────────────── */
interface UserForm {
  username: string;
  password: string;
  type: 'user' | 'admin';
  ativo: boolean;
}

function UsersSection({
  authFetch,
  users,
  reloadUsers
}: {
  authFetch: AuthFetch;
  users: User[];
  reloadUsers: () => Promise<void>;
}) {
  const [form, setForm] = useState<UserForm>({ username: '', password: '', type: 'user', ativo: true });
  const [editingId, setEditingId] = useState('');
  const [stockTarget, setStockTarget] = useState<User | null>(null);
  const [stockEntry, setStockEntry] = useState({ CTO: '', CEP: '', CEO: '' });
  const [movements, setMovements] = useState<StockMovement[]>([]);

  useEffect(() => {
    if (!stockTarget) {
      setMovements([]);
      return;
    }
    authFetch<StockMovement[]>(`/auth/users/${encodeURIComponent(stockTarget.id)}/stock/movements?limit=30`)
      .then((d) => setMovements(Array.isArray(d) ? d : []))
      .catch(() => setMovements([]));
  }, [stockTarget, authFetch]);

  const submitStock = async () => {
    if (!stockTarget) return;
    const body: Record<string, number> = {};
    (['CTO', 'CEP', 'CEO'] as const).forEach((t) => {
      const n = parseInt(stockEntry[t], 10);
      if (Number.isFinite(n) && n !== 0) body[t] = n;
    });
    if (Object.keys(body).length === 0) return window.alert('Informe ao menos uma quantidade a lançar.');
    try {
      const resp = await authFetch<{ stock: UserStock }>(`/auth/users/${encodeURIComponent(stockTarget.id)}/stock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      setStockEntry({ CTO: '', CEP: '', CEO: '' });
      // mantém o modal aberto: atualiza o saldo e recarrega o histórico (via efeito).
      setStockTarget((prev) => (prev ? { ...prev, stock: resp.stock } : prev));
      await reloadUsers();
    } catch (err) {
      window.alert((err as Error).message || 'Erro ao lançar estoque');
    }
  };

  const save = async () => {
    if (!form.username.trim()) return window.alert('Username obrigatorio.');
    if (!editingId && !form.password) return window.alert('Senha obrigatoria para novo usuario.');
    try {
      if (editingId) {
        const payload: Record<string, unknown> = { username: form.username.trim(), type: form.type, ativo: form.ativo };
        if (form.password) payload.password = form.password;
        await authFetch(`/auth/users/${encodeURIComponent(editingId)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        await authFetch('/auth/users', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: form.username.trim(), password: form.password, type: form.type, ativo: form.ativo })
        });
      }
      setForm({ username: '', password: '', type: 'user', ativo: true });
      setEditingId('');
      await reloadUsers();
    } catch (err) {
      window.alert((err as Error).message || 'Erro ao salvar user');
    }
  };

  const remove = (u: User) => {
    if (!window.confirm(`Excluir usuario ${u.username}?`)) return;
    authFetch(`/auth/users/${encodeURIComponent(u.id)}`, { method: 'DELETE' })
      .then(() => reloadUsers())
      .catch((err) => window.alert((err as Error).message || 'Erro ao excluir user'));
  };

  return (
    <div className="mx-auto max-w-4xl">
      <header className="mb-4">
        <h2 className="text-lg font-bold text-slate-800">Usuários</h2>
        <p className="text-sm text-slate-500">Crie e gerencie contas de acesso ao sistema.</p>
      </header>

      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 bg-slate-50 px-4 py-3">
          <TextInput
            className="w-36"
            placeholder="username"
            value={form.username}
            onChange={(e) => setForm((p) => ({ ...p, username: e.target.value }))}
          />
          <TextInput
            className="w-44"
            placeholder={editingId ? 'nova senha (opcional)' : 'senha'}
            type="password"
            value={form.password}
            onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
          />
          <Select
            className="w-28"
            value={form.type}
            onChange={(e) => setForm((p) => ({ ...p, type: e.target.value as 'user' | 'admin' }))}
          >
            <option value="user">user</option>
            <option value="admin">admin</option>
          </Select>
          <Select
            className="w-28"
            value={String(form.ativo)}
            onChange={(e) => setForm((p) => ({ ...p, ativo: e.target.value === 'true' }))}
          >
            <option value="true">ativo</option>
            <option value="false">inativo</option>
          </Select>
          <Button size="sm" onClick={save}>
            {editingId ? 'Salvar' : 'Criar'}
          </Button>
          {editingId && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setEditingId('');
                setForm({ username: '', password: '', type: 'user', ativo: true });
              }}
            >
              Cancelar
            </Button>
          )}
        </div>
        <div className="overflow-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className={thClass}>Username</th>
                <th className={thClass}>Tipo</th>
                <th className={thClass}>Status</th>
                <th className={thClass}>Estoque (CTO/CEP/CEO)</th>
                <th className={thClass}>Último login</th>
                <th className={thClass}></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((u) => (
                <tr key={u.id} className="hover:bg-slate-50">
                  <td className={tdClass}>{u.username}</td>
                  <td className={tdClass}>{u.type}</td>
                  <td className={tdClass}>
                    <span
                      className={
                        u.ativo
                          ? 'rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700'
                          : 'rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500'
                      }
                    >
                      {u.ativo ? 'ativo' : 'inativo'}
                    </span>
                  </td>
                  <td className={tdClass}>
                    {u.type === 'admin' ? (
                      <span className="text-xs text-slate-400">—</span>
                    ) : (
                      <div className="flex gap-1">
                        {(['CTO', 'CEP', 'CEO'] as const).map((t) => {
                          const qty = u.stock?.[t] ?? 0;
                          return (
                            <span
                              key={t}
                              className={cn(
                                'rounded px-1.5 py-0.5 text-xs font-semibold',
                                qty > 0 ? 'bg-slate-100 text-slate-700' : 'bg-red-50 text-red-500'
                              )}
                              title={t}
                            >
                              {t} {qty}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </td>
                  <td className={tdClass}>{u.lastlogin ? new Date(u.lastlogin).toLocaleString() : '-'}</td>
                  <td className={tdClass}>
                    <div className="flex gap-1">
                      {u.type !== 'admin' && (
                        <Button variant="soft" size="sm" onClick={() => setStockTarget(u)}>
                          Estoque
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditingId(u.id);
                          setForm({ username: u.username, password: '', type: u.type, ativo: u.ativo });
                        }}
                      >
                        Editar
                      </Button>
                      <Button variant="danger" size="sm" onClick={() => remove(u)}>
                        Excluir
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {stockTarget && (
        <ModalBackdrop onClose={() => setStockTarget(null)}>
          <div
            className="flex max-h-[90vh] w-full max-w-sm flex-col overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl"
          >
            <h3 className="text-lg font-bold text-slate-800">Lançar estoque</h3>
            <p className="mt-0.5 text-sm text-slate-500">
              Entrada de material para <span className="font-semibold text-slate-700">{stockTarget.username}</span> (soma ao saldo atual).
            </p>
            <div className="mt-3 flex gap-2">
              {(['CTO', 'CEP', 'CEO'] as const).map((t) => (
                <span key={t} className="rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                  {t} atual: {stockTarget.stock?.[t] ?? 0}
                </span>
              ))}
            </div>
            <div className="mt-4 grid grid-cols-3 gap-3">
              {(['CTO', 'CEP', 'CEO'] as const).map((t) => (
                <div key={t}>
                  <label className="text-xs font-semibold text-slate-600">+ {t}</label>
                  <TextInput
                    type="number"
                    inputMode="numeric"
                    placeholder="0"
                    value={stockEntry[t]}
                    onChange={(e) => setStockEntry((p) => ({ ...p, [t]: e.target.value }))}
                  />
                </div>
              ))}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setStockTarget(null)}>
                Fechar
              </Button>
              <Button onClick={submitStock}>Lançar entrada</Button>
            </div>

            <div className="mt-5 border-t border-slate-100 pt-3">
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">Histórico recente</p>
              {movements.length === 0 ? (
                <p className="text-sm text-slate-400">Sem movimentações ainda.</p>
              ) : (
                <div className="max-h-56 space-y-1.5 overflow-y-auto">
                  {movements.map((m) => (
                    <div key={m.id} className="rounded-lg border border-slate-100 px-2.5 py-1.5">
                      <div className="flex items-center gap-2 text-xs">
                        <span className={cn('font-bold', m.delta > 0 ? 'text-emerald-600' : 'text-red-600')}>
                          {m.delta > 0 ? '+' : ''}
                          {m.delta} {m.type}
                        </span>
                        <span className="text-slate-500">
                          {REASON_LABEL[m.reason] || m.reason}
                          {m.boxId ? ` · ${m.boxId}` : ''}
                        </span>
                        <span className="ml-auto whitespace-nowrap text-slate-400">
                          {new Date(m.createdAt).toLocaleString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      <div className="mt-0.5 text-[11px] text-slate-400">
                        saldo {m.balanceAfter} · por {m.changedBy || '—'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </ModalBackdrop>
      )}
    </div>
  );
}

/* ─── Cadastro genérico por nome (POPs / Cidades / Tipos) ─────── */
function NamedCrudSection({
  authFetch,
  items,
  reload,
  endpoint,
  title,
  description,
  columnLabel,
  placeholder
}: {
  authFetch: AuthFetch;
  items: NamedItem[];
  reload: () => Promise<void>;
  endpoint: string;
  title: string;
  description: string;
  columnLabel: string;
  placeholder: string;
}) {
  const [name, setName] = useState('');
  const [editingId, setEditingId] = useState('');

  const save = async () => {
    const value = name.trim();
    if (!value) return window.alert(`Nome obrigatório.`);
    try {
      if (editingId) {
        await authFetch(`${endpoint}/${encodeURIComponent(editingId)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: value })
        });
      } else {
        await authFetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: value })
        });
      }
      setName('');
      setEditingId('');
      await reload();
    } catch (err) {
      window.alert((err as Error).message || 'Erro ao salvar.');
    }
  };

  const remove = (item: NamedItem) => {
    if (!window.confirm(`Excluir "${item.name}"?`)) return;
    authFetch(`${endpoint}/${encodeURIComponent(item.id)}`, { method: 'DELETE' })
      .then(() => reload())
      .catch((err) => window.alert((err as Error).message || 'Erro ao excluir.'));
  };

  return (
    <div className="mx-auto max-w-4xl">
      <header className="mb-4">
        <h2 className="text-lg font-bold text-slate-800">{title}</h2>
        <p className="text-sm text-slate-500">{description}</p>
      </header>

      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 bg-slate-50 px-4 py-3">
          <TextInput
            className="w-64"
            placeholder={placeholder}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && save()}
          />
          <Button size="sm" onClick={save}>
            {editingId ? 'Salvar' : 'Criar'}
          </Button>
          {editingId && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setEditingId('');
                setName('');
              }}
            >
              Cancelar
            </Button>
          )}
        </div>
        <div className="overflow-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className={thClass}>{columnLabel}</th>
                <th className={thClass}></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((item) => (
                <tr key={item.id} className="hover:bg-slate-50">
                  <td className={tdClass}>{item.name}</td>
                  <td className={tdClass}>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditingId(item.id);
                          setName(item.name);
                        }}
                      >
                        Editar
                      </Button>
                      <Button variant="danger" size="sm" onClick={() => remove(item)}>
                        Excluir
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
